import { useEffect, useRef, useState } from 'react'

import { TimeSelectField } from '@fv/client-components'
import { type MilitaryTime, militaryTimeFormat } from '@fv/client-types'
import { fvDate } from '@fv/dates'

import { Pinline } from '@/components/Pinline'

import { DayPickerPanel } from '../dates/DayPickerPanel'

type DatePickerProps = {
  onClose: () => void
  value?: string
  open?: boolean
  isSaving?: boolean
  onSave: (dateTime: Date | undefined) => void
}

export const ExpirationDatePickerPanel = ({
  onClose,
  value,
  isSaving,
  open,
  onSave,
}: DatePickerProps) => {
  const selectRef = useRef<HTMLSelectElement>(null)
  const [expTime, setExpTime] = useState<string>(undefined)

  const disabledDays = (day: Date) => {
    return fvDate().startOf('day').isAfter(day)
  }

  const handleTimeSelect = (time: MilitaryTime) => {
    setExpTime(time)
  }
  const handleSave = (date: Date) => {
    if (!expTime && !date) {
      return onSave(undefined)
    }
    if (!expTime && date && selectRef.current) {
      selectRef.current.setCustomValidity('Time is required')
      selectRef.current.reportValidity()
      return
    }
    selectRef.current.setCustomValidity('')

    const expireTime = fvDate(expTime, militaryTimeFormat)
    const expireDateTime = fvDate(date)
      .set('hour', expireTime.hour())
      .set('minute', expireTime.minute())

    onSave(expireDateTime.toDate())
  }

  useEffect(() => {
    if (!open) {
      setExpTime(undefined)
    } else {
      const initialExp = value ? fvDate(value) : undefined
      setExpTime(initialExp ? initialExp.format(militaryTimeFormat) : undefined)
    }
  }, [open, value])

  return (
    <DayPickerPanel
      mode="single"
      onClose={onClose}
      open={open}
      isSaving={isSaving}
      disabled={disabledDays}
      selected={value ? fvDate(value).toDate() : undefined}
      onSave={handleSave}
      showClear
      onCancel={() => onClose()}
      onClear={() => {
        setExpTime(undefined)
      }}
      rightContent={
        <div>
          <div>{'Expiration time and date'}</div>
          <Pinline className="mt-2 mb-4" />
          <TimeSelectField
            containerClassName="col"
            ref={selectRef}
            name="endsAt"
            setValue={t => handleTimeSelect(t as MilitaryTime)}
            value={expTime}
          />
        </div>
      }
    />
  )
}
